import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  updateSwimSwitchList,
  updateIndexOnList,
  resetError,
  updateIsShowBioSimilarToggle,
  resetSwimSwitchListStateLocally,
  updateSwimSwitchListStateLocally,
  setShouldFetchOnEmptyFilters,
} from "./SwimSwitchList.actions";
import ResetListState from "components/ResetListState";
import { isLoggedInUserBioSimilarUser } from "services/AWUtilityService";
import { COLUMN } from "./CreateColumnListForSwimSwitchList";
import CreateActivity from "components/SwitchDetails/CreateActivity";
import SwimSwitchListCommon from "./SwimSwitchListCommon";
import {
  OverlayTrigger,
  Form,
  Tooltip,
  Container,
  Row,
  Col,
} from "react-bootstrap";
import { DEFAULT_SWIM_SWITCH_SORT } from "lookup/SwimConstants";
import { isLoggedInUserAWAdmin } from "services/AWUtilityService";
import DedicatedSearchForm from "commons/table/DedicatedSearchForm";

const DEFAULT_PAGE_SIZE = 15;

const COLUMN_LIST = [
  COLUMN.customerNameAbbreviation,
  COLUMN.clientNameAbbreviation,
  COLUMN.switchNumber,
  COLUMN.scheduledDate,
  COLUMN.prescriberName,
  COLUMN.prescriberFax,
  COLUMN.prescriberPhone,
  COLUMN.prescriberNpi,
  COLUMN.pharmacyName,
  COLUMN.pharmacyFax,
  COLUMN.pharmacyPhone,
  COLUMN.pharmacyNpi,
  COLUMN.type,
  COLUMN.state,
  COLUMN.activityCount,
];

const SwimSwitchList = () => {
  const [selectedSwitches, setSelectedSwitches] = useState([]);
  const [selectedPrescriberUuids, setSelectedPrescriberUuids] = useState([]);
  const [selectedPrescriberNames, setSelectedPrescriberNames] = useState([]);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);

  const searchFormRef = useRef(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    data,
    pages,
    totalElements,
    loading,
    listState,
    isError,
    isShowBioSimilarToggle,
    shouldFetchOnEmptyFilters,
  } = useSelector((state) => {
    let ls = state.swimSwitchList.listState;
    if (!ls) {
      ls = {
        page: 0,
        pageSize: DEFAULT_PAGE_SIZE,
        sorted: DEFAULT_SWIM_SWITCH_SORT,
        filtered: [],
        resized: [],
        expanded: {},
      };
    }
    return {
      data:
        state.swimSwitchList.data !== undefined
          ? state.swimSwitchList.data
          : [],
      pages: state.swimSwitchList.pages ? state.swimSwitchList.pages : 0,
      totalElements: state.swimSwitchList.loading
        ? "Loading ..."
        : state.swimSwitchList.totalElements !== undefined
        ? state.swimSwitchList.totalElements
        : "0",
      loading: state.swimSwitchList.loading,
      listState: ls,
      isError: state.swimSwitchList.isError,
      isShowBioSimilarToggle:
        state.swimSwitchList.isShowBioSimilar ?? undefined,
      shouldFetchOnEmptyFilters:
        state.swimSwitchList.shouldFetchOnEmptyFilters ?? false,
    };
  });

  const effectiveListState = location.state ? location.state : listState;

  const resetList = useCallback(() => {
    const { pageSize, resized, expanded } = listState;
    const newFiltered = [];
    const bioSimilarToggle = false;

    dispatch(setShouldFetchOnEmptyFilters(false));

    // Clear location.state equivalent in React Router v7
    navigate(location.pathname, { replace: true, state: null });

    dispatch(
      resetSwimSwitchListStateLocally(
      pageSize,
      0,
      DEFAULT_SWIM_SWITCH_SORT,
      newFiltered,
      resized,
      expanded
      )
    );
    dispatch(updateIsShowBioSimilarToggle(bioSimilarToggle));

    if (searchFormRef.current) {
      searchFormRef.current.resetForm();
    }
  }, [dispatch, listState, location, navigate]);

  useEffect(() => {
    setIsInitialLoadComplete(true);
    if (isShowBioSimilarToggle === undefined) {
      let bioSimilarToggle = false;
      const isBioSimilarUser = isLoggedInUserBioSimilarUser();
      if (isBioSimilarUser && !isLoggedInUserAWAdmin()) {
        bioSimilarToggle = true;
      }
      dispatch(updateIsShowBioSimilarToggle(bioSimilarToggle));
    }
  }, [dispatch, isShowBioSimilarToggle]);

  const handleBioSimilarToggle = useCallback(
    (e) => {
      const { filtered, pageSize, resized, expanded } = listState;
    let newFiltered = filtered.filter(
      (element) =>
        ![
          "switchNumber",
          "priorityAgent",
          "customerNameAbbreviation",
          "clientNameAbbreviation",
        ].includes(element.id)
    );
      const switchNumberFilter = filtered.filter(
        (f) => f.id === "switchNumber"
      );
    if (switchNumberFilter.length > 0) {
        newFiltered.push({
          id: "switchNumberAsString",
          value: switchNumberFilter[0].value,
          operator: "STARTS_WITH",
        });
    }

    if (e.target.checked) {
      newFiltered.push(
        { id: "priorityAgent", value: true },
        { id: "customerNameAbbreviation", value: "WMT" },
        { id: "clientNameAbbreviation", value: "WMT" }
      );
      if (JSON.stringify(newFiltered) !== JSON.stringify(filtered)) {
          dispatch(setShouldFetchOnEmptyFilters(true));
          dispatch(
            updateSwimSwitchListStateLocally(
          pageSize,
              listState.page,
              listState.sorted,
          newFiltered,
          resized,
          expanded
            )
        );
      }
    } else {
      if (newFiltered.length > 0) {
        newFiltered.push({ id: "priorityAgent", value: false });
          if (
            JSON.stringify(newFiltered) !== JSON.stringify(filtered) &&
            newFiltered.length > 1
          ) {
            dispatch(setShouldFetchOnEmptyFilters(true));
            dispatch(
              updateSwimSwitchListStateLocally(
            pageSize,
                listState.page,
                listState.sorted,
            newFiltered,
            resized,
            expanded
              )
          );
        } else {
            dispatch(
              resetSwimSwitchListStateLocally(
            pageSize,
            0,
            DEFAULT_SWIM_SWITCH_SORT,
            newFiltered,
            resized,
            expanded
              )
          );
        }
      }
    }
      dispatch(updateIsShowBioSimilarToggle(e.target.checked));
    },
    [dispatch, listState]
  );

  const toggleSelection = useCallback(
    (selected) => {
      const selectedIds = Object.keys(selected);
      const prescriberData = data.reduce(
      (acc, row) => {
          if (selectedIds.includes(row.wfItemId)) {
          acc.uuids.add(row.prescriberUuid);
          acc.names.add(row.prescriberName);
        }
        return acc;
      },
      { uuids: new Set(), names: new Set() }
    );
      setSelectedSwitches(selectedIds);
      setSelectedPrescriberUuids(Array.from(prescriberData.uuids));
      setSelectedPrescriberNames(Array.from(prescriberData.names));
    },
    [data]
  );

  const handleSearchSubmit = useCallback(
    (searchForm, selectedSearchType) => {
    const { switchId, npi, phone, fax, name, state } = searchForm;
    const searchType = selectedSearchType.value;

      let newFiltered = [...listState.filtered];
      newFiltered = newFiltered.filter(
        (item) =>
          ![
            "switchNumber",
            "switchNumberAsString",
            "prescriberNpi",
            "prescriberPhone",
            "prescriberFax",
            "prescriberName",
            "pharmacyNpi",
            "pharmacyPhone",
            "pharmacyFax",
            "pharmacyName",
            "state",
          ].includes(item.id)
      );

      if (switchId) {
        newFiltered.push({
          id: "switchNumberAsString",
          value: switchId,
          operator: "STARTS_WITH",
        });
      }

    const getFilterFromSearchField = (fieldName, fieldValue) => {
      if (fieldValue) {
          newFiltered.push({
            id: `${searchType}${fieldName}`,
            value: fieldValue,
          });
      }
    };

      getFilterFromSearchField("Npi", npi);
      getFilterFromSearchField("Phone", phone);
      getFilterFromSearchField("Fax", fax);
      getFilterFromSearchField("Name", name);

    if (state) {
        const selectedStates = state.map((item) => item.value);
      newFiltered.push({ id: "state", value: selectedStates });
    }

      const bioSimilarFilters = newFiltered.filter((e) =>
        ["priorityAgent", "customerNameAbbreviation", "clientNameAbbreviation"].includes(
          e.id
        )
      );
      if (bioSimilarFilters.length === 0) {
      newFiltered.push({ id: "priorityAgent", value: false });
    }

      dispatch(setShouldFetchOnEmptyFilters(true));

      dispatch(
        updateSwimSwitchListStateLocally(
          listState.pageSize,
          0,
          listState.sorted,
      newFiltered,
          listState.resized,
          listState.expanded
        )
    );
    },
    [dispatch, listState]
  );

    return (
      <Container className="list-container" fluid>
        <Row>
          <Col md={12}>
            <div
              className="codex-version-display-bar"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <span>
                Total Switches: <strong>{totalElements}</strong>
                </span>
                <span style={{ paddingLeft: "2vh" }}>
                  <CreateActivity
                    wfItemId={
                    selectedSwitches.length > 0 ? selectedSwitches[0] : null
                    }
                    isMemberActivityCreatable={false}
                  disableButton={selectedSwitches.length === 0}
                  selectedSwitches={selectedSwitches}
                    openModalButtonStyle="warning"
                    contextIsAW={true}
                  selectedPrescriberUuids={selectedPrescriberUuids}
                  selectedPrescriberNames={selectedPrescriberNames}
                  />
                </span>
              </div>
              <strong style={{ textAlign: "center" }}>Swim Switch List</strong>
              <div style={{ display: "flex", alignItems: "center" }}>
                {isLoggedInUserBioSimilarUser() && (
                  <span
                    className="tiny-bottom-spacer"
                    style={{ marginRight: "15px" }}
                  >
                    <OverlayTrigger
                      overlay={
                        <Tooltip>
                          Toggling this ON will show Bio Similar Switches
                        </Tooltip>
                      }
                    >
                      <Form.Check
                        type="switch"
                        className="aw-bio-similar-callgroups-toggle"
                        id="toggleBioSimilarRef"
                        label={`Bio Similar Switches`}
                      onChange={handleBioSimilarToggle}
                      checked={!!isShowBioSimilarToggle}
                      />
                    </OverlayTrigger>
                  </span>
                )}
              <ResetListState onReset={resetList} />
              </div>
            </div>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <DedicatedSearchForm
            ref={searchFormRef}
            onSubmit={handleSearchSubmit}
            isLoading={loading}
            />
          </Col>
        </Row>
        <SwimSwitchListCommon
        updateInterventionList={(pageSize, page, sorted, filtered, resized, expanded) =>
          dispatch(
            updateSwimSwitchList(
              pageSize,
              page,
              sorted,
              filtered,
              resized,
              expanded
            )
          )
        }
        updateIndexOnList={(index) => dispatch(updateIndexOnList(index))}
        resetSwimSwitchListStateLocally={(
          pageSize,
          page,
          sorted,
          filtered,
          resized,
          expanded
        ) =>
          dispatch(
            resetSwimSwitchListStateLocally(
              pageSize,
              page,
              sorted,
              filtered,
              resized,
              expanded
            )
          )
        }
        resetError={() => dispatch(resetError())}
        data={data}
        pages={pages}
        totalElements={totalElements}
        loading={loading}
        listState={effectiveListState}
        isError={isError}
          columnList={COLUMN_LIST}
          isSortFilterEnabledForCust={true}
        toggleSelection={toggleSelection}
        isInitialLoadComplete={isInitialLoadComplete}
        isShowBioSimilarToggle={isShowBioSimilarToggle}
        shouldFetchOnEmptyFilters={shouldFetchOnEmptyFilters}
        />
      </Container>
    );
};

export default SwimSwitchList;
